/**
 * The week before Friday.
 *
 * The ministries publish about a week ahead, which is what makes this
 * section something better defined than "learn Arabic": a bounded, dated
 * preparation for one event, with a finish line that arrives weekly.
 *
 * So the numbers here are about **Friday**, not about points.
 */

export function daysUntil(friday: string, now: Date = new Date()): number {
  const target = new Date(`${friday}T00:00:00`);
  if (Number.isNaN(target.getTime())) {
    return 0;
  }
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

/**
 * What the countdown says.
 *
 * A khutbah that has already been delivered is not "overdue" — it is simply
 * past, and still worth studying. Saying "-3 days" would be nonsense.
 */
export function countdown(friday: string, now: Date = new Date()): string {
  const days = daysUntil(friday, now);
  if (days > 1) return `${days} days until Friday`;
  if (days === 1) return 'Tomorrow';
  if (days === 0) return 'Today';
  if (days === -1) return 'Delivered yesterday';
  return `Delivered ${Math.abs(days)} days ago`;
}

/**
 * How much of the khutbah a learner could follow, from what is glossed.
 *
 * Deliberately a statement about **coverage**, not about the learner: no
 * per-user progress exists, and the brief forbids fluency claims. This says
 * what the content can support, which is honest and answerable today.
 */
export function readiness(glossed: number, words: number): number {
  return words === 0 ? 0 : Math.round((glossed / words) * 100);
}

/** The sentence under the countdown. Never a fluency claim. */
export function readinessLine(glossed: number, words: number): string {
  if (words === 0) {
    return 'No words of this khutbah have been prepared for learning yet.';
  }
  return `${glossed} of ${words} words carry a meaning — about ${readiness(
    glossed,
    words,
  )}% of this khutbah.`;
}
